import { useEffect, useMemo, useState } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { useVideos } from "@/hooks/useVideos";
import { useSeries } from "@/hooks/useSeries";
import { Sidebar } from "@/components/Sidebar";
import { cn } from "@/lib/utils";
import { Loader2, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { VideoGrid } from "@/components/VideoGrid";
import type { Video } from "@/types/video";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { useMembershipStatus } from "@/hooks/useMembershipStatus";
import { useTrialSeries } from "@/hooks/useTrialSeries";
import { useAudiosBySeries } from "@/hooks/useAudios";
import { isLiveDemoBlockedSeriesTitle } from "@/lib/live-demo";
import { isLiveDemoBlockedVideo, readLiveDemoConfig } from "@/lib/live-demo-config";

export default function Series() {
  const { id = "" } = useParams<{ id: string }>();
  const location = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const { data: series, isLoading: isSeriesLoading } = useSeries(id);
  const { data: allVideos = [], isLoading: isVideosLoading } = useVideos();
  const { data: seriesAudios = [], isLoading: isAudiosLoading } = useAudiosBySeries(id);
  const { toast } = useToast();
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const { data: membership, isLoading: isMembershipLoading } = useMembershipStatus(user?.id);
  const { data: trial, isLoading: isTrialLoading } = useTrialSeries();
  const isLiveDemoMode = useMemo(() => {
    if (!membership?.isAdmin) return false;
    const params = new URLSearchParams(location.search);
    return params.get("live") === "1";
  }, [location.search, membership?.isAdmin]);
  const isBlockedSeriesForLive = useMemo(
    () => isLiveDemoBlockedSeriesTitle(series?.title),
    [series?.title],
  );
  const isLockedToTrial =
    !membership?.isPremium && !!trial?.trialSeriesId && trial.trialSeriesId !== id;

  const seriesVideos = useMemo(() => {
    const videos = allVideos
      .filter((v) => v.series === id)
      .sort((a, b) => (a.publishedAt || "").localeCompare(b.publishedAt || ""));
      
    const coverUrl = (series as any)?.cover_url as string | undefined;
    
    const audios = (seriesAudios || []).map((a) => ({
      ...a,
      thumbnailUrl: a.thumbnailUrl || coverUrl || "",
      contentType: "audio", // Audios are always audio
    }));

    const combined = [...videos, ...audios].map((item) => {
      if (series?.title === "人気の童話" && coverUrl) {
        const isMissingThumb = !item.thumbnailUrl || item.thumbnailUrl === item.videoUrl;
        if (isMissingThumb) return { ...item, thumbnailUrl: coverUrl };
      }
      return item;
    }).sort((a, b) => {
      const ao = Number((a as any).episodeNumber || 0);
      const bo = Number((b as any).episodeNumber || 0);
      if (ao !== bo) return ao - bo;
      return (a.publishedAt || "").localeCompare(b.publishedAt || "");
    });
    return combined;
  }, [allVideos, id, series, seriesAudios]);

  const totalDurationLabel = useMemo(() => {
    const totalSeconds = seriesVideos.reduce((sum, v) => sum + (v.durationSeconds || 0), 0);
    if (!totalSeconds) return "";
    const hours = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;
    if (hours > 0) return `${hours}:${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  }, [seriesVideos]);

  const handleVideoClick = (video: Video) => {
    if (isLiveDemoMode && isBlockedSeriesForLive) {
      toast({ title: "直播演示：该系列仅浏览，不进入播放" });
      return;
    }
    if (isLiveDemoMode) {
      const liveConfig = readLiveDemoConfig();
      if (isLiveDemoBlockedVideo(liveConfig, video.id, video.title)) {
        toast({ title: "Live demo: blocked video skipped" });
        return;
      }
    }
    if (video.isLocked && !user) {
      toast({ title: "需要登录", description: "请登录后观看此内容", variant: "destructive" });
      navigate("/auth");
      return;
    }
    // Always navigate to Player page, even for audio content
    // The Player page now handles audio content correctly
    navigate(
      isLiveDemoMode ? `/player/${video.id}?live=1&autoplay=1` : `/player/${video.id}`
    );
  };

  const handleLogout = async () => {
    await signOut();
    toast({ title: "已退出登录" });
  };

  const isLoading = isSeriesLoading || isVideosLoading || isAudiosLoading || isMembershipLoading || isTrialLoading;

  useEffect(() => {
    if (!isLiveDemoMode) return;
    if (isLoading) return;

    const liveConfig = readLiveDemoConfig();
    const basePauseMs = Math.max(2500, liveConfig.seriesPauseMs || 10000);
    const jitterMs = Math.floor((Math.random() - 0.5) * 2600);
    const delayMs = Math.max(2200, basePauseMs + jitterMs);

    const timer = window.setTimeout(() => {
      if (isBlockedSeriesForLive || seriesVideos.length === 0) {
        navigate(`/?live=1&excludeSeries=${encodeURIComponent(id)}`, { replace: true });
        return;
      }
      const playable = seriesVideos.filter(
        (item) => !isLiveDemoBlockedVideo(liveConfig, item.id, (item as any).title),
      );
      if (playable.length === 0) {
        navigate(`/?live=1&excludeSeries=${encodeURIComponent(id)}`, { replace: true });
        return;
      }
      const target = playable[Math.floor(Math.random() * playable.length)];
      if (!target?.id) return;
      navigate(`/player/${target.id}?live=1&autoplay=1`, { replace: true });
    }, delayMs);

    return () => window.clearTimeout(timer);
  }, [id, isBlockedSeriesForLive, isLiveDemoMode, isLoading, navigate, seriesVideos]);

  if (!isLoading && !series) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center">
         <p className="text-muted-foreground mb-4">未找到该系列</p>
         <Button onClick={() => navigate("/")}>返回首页</Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Sidebar
        isCollapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        currentPage="series"
        onNavigate={(p) => p === "home" ? navigate("/") : navigate(`/${p}`)}
        isLoggedIn={!!user}
        onLogout={handleLogout}
      />

      <main
        className={cn(
          "transition-all duration-300",
          "lg:ml-16",
          !sidebarCollapsed && "lg:ml-60"
        )}
      >
        <div className="px-4 py-6 lg:px-6 lg:py-8 max-w-[1920px] mx-auto">
          <div className="flex flex-col gap-2 mb-6">
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => navigate("/")}>
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <Button variant="ghost" className="h-9 px-2" onClick={() => navigate("/")}>
                返回列表
              </Button>
            </div>
            <h1 className="text-xl lg:text-2xl font-bold text-foreground line-clamp-1">
              {series?.title || "系列"}
            </h1>
            {isLiveDemoMode ? (
              <div className="inline-flex w-fit rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
                直播演示中
              </div>
            ) : null}
            <div className="text-sm text-muted-foreground">
              <span>共 {seriesVideos.length} 集</span>
              {totalDurationLabel && <span> · 总时长 {totalDurationLabel}</span>}
            </div>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <span className="ml-2 text-muted-foreground">加载中...</span>
            </div>
          ) : isLockedToTrial ? (
            <div className="text-center py-20">
              <p className="text-muted-foreground mb-4">普通用户仅可观看试看系列</p>
              <Button onClick={() => navigate("/profile")}>去会员升级</Button>
            </div>
          ) : seriesVideos.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-muted-foreground">该系列暂无视频</p>
            </div>
          ) : (
            <VideoGrid
              videos={seriesVideos}
              onVideoClick={handleVideoClick}
              showCategoryBadges={false}
            />
          )}
        </div>
      </main>
    </div>
  );
}
